import { FunctionComponent, memo, useEffect, useState } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import nanoid from '@utils/random'
import { AdSlide } from '@widgets/Gallery/types'
import { StyledWrapper as AdInnerWrapper } from '@components/Ad/Wrapper'
import JSONRenderer from '@components/JSONRenderer'

type GalleryAdSlideProps = {
  adSlide: AdSlide
  galleryUUID: string
  index: number
  activeIndex: number
}

const GalleryAdSlideWrapper = styled.div<{ shouldBePreloaded: boolean }>`
  ${({
    theme: {
      colors: { staticWhite, staticBlack },
    },
    shouldBePreloaded,
  }) => css`
    width: 100%;
    height: 100%;
    display: grid;
    justify-items: center;
    align-items: center;
    background-color: ${staticBlack};
    ${desktopCSS(css`
      background-color: ${staticWhite};
    `)}

    ${shouldBePreloaded &&
    css`
      position: fixed;
      top: 200vh;
      left: 0;
      height: 0;
      overflow: hidden;
      ${desktopCSS(css`
        width: 632px;
      `)};

      ${tabletCSS(css`
        width: 632px;
      `)};

      ${mobileCSS(css`
        width: calc(100vw - 2 * 16px);
      `)};
    `};

    //! This is very important to be here, otherwise the ad container will initially
    //! have a width of 0, which can hurt viewability metrics.
    > ${AdInnerWrapper} {
      width: 100%;
      text-align: center;
    }
  `}
`

const GalleryAdSlide: FunctionComponent<GalleryAdSlideProps> = (props) => {
  const { adSlide, galleryUUID, index, activeIndex } = props
  const [adKey, setAdKey] = useState<string>('')

  const shouldBeRegistered =
    index >= activeIndex - 2 && index <= activeIndex + 2

  const shouldBePreloaded =
    index === activeIndex - 2 || index === activeIndex + 2

  useEffect(() => {
    if (shouldBeRegistered) {
      setAdKey(nanoid())
    } else {
      setAdKey('')
    }
  }, [shouldBeRegistered])

  return (
    <GalleryAdSlideWrapper shouldBePreloaded={shouldBePreloaded}>
      {shouldBeRegistered && adKey && (
        <JSONRenderer key={adKey}>
          {{
            ...adSlide,
            containerId: `${galleryUUID}-${index}-${adKey}`,
          }}
        </JSONRenderer>
      )}
    </GalleryAdSlideWrapper>
  )
}

const MemoizedGalleryAdSlide = memo(GalleryAdSlide)

MemoizedGalleryAdSlide.displayName = 'MemoizedGalleryAdSlide'

export default MemoizedGalleryAdSlide
